import React from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { CrewModuleId } from '../../../shared/types';
import { useCrew, useCrewModuleAccess } from '../hooks/useCrews';

interface ModuleGuardProps {
    /** A single module, or a list — the route is allowed if ANY of them is enabled. */
    moduleId: CrewModuleId | CrewModuleId[];
    children: React.ReactNode;
}

/**
 * Route guard for a crew module's pages. Redirects to the crew home when the
 * module(s) are disabled for the crew (tier/crewType gate, manual toggle, or a
 * disabled dependency), so a hand-typed or bookmarked URL to a turned-off
 * module lands somewhere sensible instead of rendering a 403.
 *
 * Pass an array for surfaces shared by multiple modules (allowed if any is on).
 *
 * Waits for the crew query to load before deciding — the module hooks fail
 * closed while loading, so redirecting early would bounce valid deep links.
 */
export const ModuleGuard: React.FC<ModuleGuardProps> = ({ moduleId, children }) => {
    const { slug } = useParams<{ slug: string }>();
    const { data: crew } = useCrew(slug!);
    const moduleAccess = useCrewModuleAccess(slug!);

    // Crew not loaded yet — render children rather than block; the module's own
    // data hooks stay gated (fail closed) until the crew resolves, so nothing 403s.
    if (!crew) return <>{children}</>;

    const ids = Array.isArray(moduleId) ? moduleId : [moduleId];
    const enabled = ids.some(id => moduleAccess[id]);

    if (!enabled) return <Navigate to={`/c/${slug}`} replace />;

    return <>{children}</>;
};
